import { derived, readable, type Readable } from 'svelte/store';
import { createQuery, type QueryClient } from '$lib/api/tanstack-query';
import type { FleetDashboardServiceClient } from '@viamrobotics/web-client/fleetdashboard/v1/fleetdashboard.client';
import { ConfigurationBlockKind } from '@viamrobotics/web-client/fleetdashboard/v1/fleetdashboard';

import { AuthStatus, type AuthContext, useAuthContext } from '$lib/auth';
import { useClient } from './client';

import { prefetchQuery } from './prefetch';
import { createQueryOptions } from './query-options';
import { registryAdminKeys } from './registry-admin/query-keys';

/** Matches the server SearchConfigurationBlocks cache TTL. */
export const SEARCH_CONFIGURATION_BLOCKS_STALE_TIME_MS = 60_000;

/** Kinds used by machine and fragment add-block search. */
export const ADD_BLOCK_SEARCH_KINDS: ConfigurationBlockKind[] = [
  ConfigurationBlockKind.MODULE,
  ConfigurationBlockKind.FRAGMENT,
  ConfigurationBlockKind.ML_MODEL,
];

/** All search corpora. Used by the registry browse page. */
export const REGISTRY_SEARCH_KINDS: ConfigurationBlockKind[] = [
  ConfigurationBlockKind.MODULE,
  ConfigurationBlockKind.FRAGMENT,
  ConfigurationBlockKind.ML_MODEL,
  ConfigurationBlockKind.ML_TRAINING,
  ConfigurationBlockKind.APPLICATION,
];

export interface SearchConfigurationBlocksOptions {
  kinds?: readonly ConfigurationBlockKind[];
  includeDeprecated?: boolean;
}

/**
 * Search configuration blocks (modules, fragments, builtins, ML models,
 * training scripts, and Viam applications). Public results work without auth; private org items are
 * included when logged in.
 */
export const createSearchConfigurationBlocksQuery = (
  searchTerm: Readable<string>,
  organizationID: Readable<string | undefined>,
  options?: Readable<SearchConfigurationBlocksOptions>
) => {
  const client = useClient('fleetDashboard');
  const authContext = useAuthContext();

  const queryOptions = derived(
    [
      searchTerm,
      organizationID,
      authContext.authState,
      options ?? readable({}),
    ],
    ([$searchTerm, $organizationID, $authState, $options]) =>
      searchConfigurationBlocksQueryOptions(
        client,
        authContext,
        $searchTerm,
        $organizationID,
        // Wait for auth to settle so logged-in users get org-scoped private items.
        $authState.status !== AuthStatus.Loading,
        $options
      )
  );

  return createQuery(queryOptions);
};

export const prefetchSearchConfigurationBlocks = async (
  queryClient: QueryClient,
  client: FleetDashboardServiceClient,
  authContext: AuthContext,
  organizationId: string | undefined,
  options?: SearchConfigurationBlocksOptions
) => {
  const queryOptions = searchConfigurationBlocksQueryOptions(
    client,
    authContext,
    '',
    organizationId,
    true,
    options
  );
  return prefetchQuery(queryClient, queryOptions);
};

/**
 * Warm the default /registry browse query (empty term, all kinds, not
 * deprecated) for the current org. No-op while auth is still loading or when
 * the fleet-dashboard client is unavailable (SSR). Fire-and-forget from the
 * app shell; TanStack will not refetch while the result is fresh.
 */
export const prefetchDefaultRegistrySearch = async (
  queryClient: QueryClient,
  client: FleetDashboardServiceClient | undefined,
  authContext: AuthContext,
  organizationId: string | undefined,
  authStatus: AuthStatus
): Promise<void> => {
  if (!client || authStatus === AuthStatus.Loading) {
    return;
  }
  await prefetchSearchConfigurationBlocks(
    queryClient,
    client,
    authContext,
    organizationId,
    { kinds: REGISTRY_SEARCH_KINDS }
  );
};

export const searchConfigurationBlocksQueryOptions = (
  client: FleetDashboardServiceClient,
  authContext: AuthContext,
  searchTerm: string,
  organizationId: string | undefined,
  enabled: boolean,
  options: SearchConfigurationBlocksOptions = {}
) => {
  const kinds = options.kinds ? [...options.kinds] : undefined;
  const includeDeprecated = options.includeDeprecated ?? false;
  return createQueryOptions({
    queryKey: [
      ...registryAdminKeys.list(),
      {
        searchTerm,
        organizationId: organizationId ?? null,
        kinds: kinds ?? null,
        includeDeprecated,
      },
    ],
    enabled,
    staleTime: SEARCH_CONFIGURATION_BLOCKS_STALE_TIME_MS,
    queryFn: async () => {
      const rpcOptions = await authContext.getOptionalRPCOptions();
      const { response } = await client.searchConfigurationBlocks(
        {
          searchTerm,
          organizationId,
          // Empty kinds is the proto default (module + fragment).
          kinds: kinds ?? [],
          includeDeprecated,
        },
        rpcOptions
      );
      return response;
    },
  });
};
